import { z } from 'zod';
import { STOCK_STATUSES } from '../materials/stock.js';
import { ALLERGENS, COST_CENTERS, STORAGE_TYPES } from './catalog.js';
import { DISPOSAL_REASONS, EXPIRY_STATES } from './lots.js';

/**
 * Kumanya API'sinin istek şemaları (ADR 0099): gemi sunucusu, bulut ve arayüz ortak kullanır.
 * Sorgu dizgeleri `z.coerce` ile çözülür.
 */

const day = z.iso.date();
const flag = z
  .enum(['true', 'false'])
  .transform((value) => value === 'true')
  .optional();
const quantity = z.number().positive().max(1_000_000_000);

export const PROVISION_SORT_KEYS = ['category', 'name', 'onHand', 'status', 'expiry'] as const;

/** Kumanya listesi süzgeçleri (Provision List). */
export const provisionItemQuerySchema = z.strictObject({
  q: z.string().max(200).optional(),
  categoryCode: z.string().max(20).optional(),
  categoryId: z.uuid().optional(),
  storageType: z.enum(STORAGE_TYPES).optional(),
  costCenter: z.enum(COST_CENTERS).optional(),
  allergen: z.enum(ALLERGENS).optional(),
  status: z.enum(STOCK_STATUSES).optional(),
  expiry: z.enum(['expiring', 'expired']).optional(),
  stockOnly: flag,
  includeInactive: flag,
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(200).optional(),
  sort: z.enum(PROVISION_SORT_KEYS).optional(),
  direction: z.enum(['asc', 'desc']).optional(),
});

export const provisionLotQuerySchema = z.strictObject({
  stockItemId: z.uuid().optional(),
  state: z.enum(EXPIRY_STATES).optional(),
  includeEmpty: flag,
});

export const provisionMovementQuerySchema = z.strictObject({
  stockItemId: z.uuid().optional(),
  lotId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
});

/** Elle giriş (Stock In) ya da açılış stoğu. */
export const provisionReceiptSchema = z.strictObject({
  materialId: z.uuid(),
  quantity,
  batchNo: z.string().max(100).nullish(),
  expiryDate: day.nullish(),
  receivedDate: day.optional(),
  source: z.enum(['manual', 'opening']).optional(),
  location: z.string().max(200).nullish(),
  reason: z.string().max(500).nullish(),
  unitPrice: z.number().min(0).max(1_000_000_000).nullish(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullish(),
});

/** Tüketim (Stock Out): lot verilmezse FEFO. */
export const provisionIssueSchema = z.strictObject({
  stockItemId: z.uuid(),
  quantity,
  lotId: z.uuid().nullish(),
  reason: z.string().max(500).nullish(),
});

/** İmha/zayi. */
export const provisionDisposalSchema = z.strictObject({
  stockItemId: z.uuid(),
  lotId: z.uuid().nullish(),
  quantity,
  reasonCode: z.enum(DISPOSAL_REASONS),
  reason: z.string().min(1).max(500),
});

/** Sayım: lot başına sayılan miktar (`lotId: null` lotsuz bakiye). */
export const provisionCountSchema = z.strictObject({
  stockItemId: z.uuid(),
  counts: z
    .array(
      z.strictObject({ lotId: z.uuid().nullable(), counted: z.number().min(0).max(1_000_000_000) }),
    )
    .min(1)
    .max(500),
  reason: z.string().min(1).max(500),
});

/** İaşe bütçesi (ADR 0123 §5): para birimi başına günlük kişi başı oran; birim tekrarlanmaz. */
export const victuallingBudgetsSchema = z
  .array(
    z.strictObject({
      currency: z.string().regex(/^[A-Z]{3}$/),
      dailyRate: z.number().positive().max(100_000),
    }),
  )
  .max(10)
  .refine((list) => new Set(list.map((b) => b.currency)).size === list.length, {
    message: 'DUPLICATE_CURRENCY',
  });

/** Uyarı penceresi ayarı; iaşe bütçesi verilmezse değişmez. */
export const provisionSettingsSchema = z.strictObject({
  rowVersion: z.number().int().positive().nullable(),
  expiryWarningDays: z.number().int().min(1).max(365),
  victuallingBudgets: victuallingBudgetsSchema.optional(),
});

/** Aylık iaşe raporu (ADR 0123): ay `YYYY-MM`. */
export const victuallingQuerySchema = z.strictObject({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
});

/** Kütüphanenin uygulanması. */
export const provisionLibraryApplySchema = z.strictObject({
  dryRun: z.boolean().optional(),
});
